import * as Yup from 'yup'
import _, { isEmpty } from 'lodash'

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
  }
}

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = _.get(item, propertyPath)
      if (
        propertyValue &&
        _.filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!_.isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

export type OfferRecipientFieldMessages = {
  firstNameRequired: string
  lastNameRequired: string
  emailRequired: string
  emailInvalid: string
  duplicateEmail: string
}

export const recipientRowHasAnyValue = (recipient: {
  firstName?: string
  lastName?: string
  title?: string | null
  email?: string
}) =>
  !isEmpty(recipient.firstName) ||
  !isEmpty(recipient.lastName) ||
  !isEmpty(recipient.email) ||
  !isEmpty(recipient.title)

const createOfferRecipientItemValidation = (
  messages: OfferRecipientFieldMessages
) =>
  Yup.object().shape({
    firstName: Yup.string().test(
      'conditional-required',
      messages.firstNameRequired,
      function (value) {
        const hasAnyValue = recipientRowHasAnyValue(this.parent)
        if (hasAnyValue && isEmpty(value)) {
          return false
        }
        return true
      }
    ),
    lastName: Yup.string().test(
      'conditional-required',
      messages.lastNameRequired,
      function (value) {
        const hasAnyValue = recipientRowHasAnyValue(this.parent)
        if (hasAnyValue && isEmpty(value)) {
          return false
        }
        return true
      }
    ),
    title: Yup.string().nullable(),
    email: Yup.string()
      .test(
        'conditional-email-format',
        messages.emailInvalid,
        function (value) {
          const hasAnyValue = recipientRowHasAnyValue(this.parent)
          if (hasAnyValue && !isEmpty(value)) {
            return Yup.string().email().isValidSync(value)
          }
          return true
        }
      )
      .test('conditional-required', messages.emailRequired, function (value) {
        const hasAnyValue = recipientRowHasAnyValue(this.parent)
        if (hasAnyValue && isEmpty(value)) {
          return false
        }
        return true
      }),
  })

export const buildOfferRecipientsValidation = (
  messages: OfferRecipientFieldMessages
) =>
  Yup.array()
    .of(createOfferRecipientItemValidation(messages))
    .test('all-recipients-empty', '', function (recipients) {
      const list = recipients ?? []
      if (list.some(recipientRowHasAnyValue)) {
        return true
      }

      if (list.length === 0) {
        throw new Yup.ValidationError([
          this.createError({
            path: this.path,
            message: messages.firstNameRequired,
          }),
        ])
      }

      const errors: Yup.ValidationError[] = []
      list.forEach((_, index) => {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].firstName`,
            message: messages.firstNameRequired,
          }),
          this.createError({
            path: `${this.path}[${index}].lastName`,
            message: messages.lastNameRequired,
          }),
          this.createError({
            path: `${this.path}[${index}].email`,
            message: messages.emailRequired,
          })
        )
      })
      throw new Yup.ValidationError(errors)
    })
    .uniqueProperty('email', messages.duplicateEmail)
